/**
 * Cache for pre-started try-on requests.
 *
 * Lets PhotoCapture kick off the try-on API call as soon as the user
 * confirms the photo, so by the time they reach TryOnResult the request
 * is already in progress (or done). Saves ~20-30s of perceived wait.
 */

// Module-level singleton — keyed by sessionId
const cache = new Map();

/**
 * Start a try-on request in the background.
 * Returns the sessionId used for this request.
 */
export function startTryOnInBackground(sessionId, promise) {
  if (!sessionId) return null;
  cache.set(sessionId, {
    promise,
    startedAt: Date.now(),
    status: 'pending'
  });
  promise
    .then((result) => {
      const entry = cache.get(sessionId);
      if (entry) {
        entry.status = 'done';
        entry.result = result;
      }
    })
    .catch((err) => {
      const entry = cache.get(sessionId);
      if (entry) {
        entry.status = 'error';
        entry.error = err;
      }
    });
  return sessionId;
}

/**
 * Retrieve a previously started try-on promise.
 * Returns null if no pending or done entry exists.
 */
export function getCachedTryOn(sessionId) {
  if (!sessionId) return null;
  const entry = cache.get(sessionId);
  if (!entry) return null;
  return entry;
}

/**
 * Clear a cached entry (after consumption or on error).
 */
export function clearCachedTryOn(sessionId) {
  if (sessionId) cache.delete(sessionId);
}

/**
 * Generate a fresh sessionId for a new try-on flow.
 */
export function generateSessionId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
